/* eslint-disable @typescript-eslint/no-explicit-any */
import kebabCase from 'lodash/kebabCase';
import {memo, useEffect, useMemo, useRef, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
// import {Image} from '@jobseeker/lotus/components/Image';
import {Grid, Link, Typography} from '@mui/material';

import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';

import {Chat} from '@components';
import {metaInfo} from '@configs';
import {CATEGORY, JOBFEED, RECRUITER_JOBFEED} from '@constants';
import {usePageLoad} from '@hooks';
import {Box} from '@jobseeker/lotus/foundations';
import {JobFeed, NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp, isRecruiterStale} from '@utils';
import moment from 'moment';

// import InjectSearchOverlay from '@components/SearchInjection';
import {loadSEO} from '@helpers';
import {wrapper} from '@jobseeker/common-core';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {jobfeedService} from '@jobseeker/common-core/services';
import {handleTracking, TrackEventUBA} from '@jobseeker/common-core/utils';
import {CTA, UserProfileCard} from '@jobseeker/lotus/components';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useIsLargeDevice, useWrappedTheme} from '@jobseeker/lotus/hooks';
import type {GetServerSidePropsContext} from 'next/types';
import {useRouter} from 'next/router';
import {FaLinkedin} from 'react-icons/fa';

import type {AppState} from '@jobseeker/common-core/types';
import Header from '@jobseeker/feature-job/components/Recruiter/Header';
import dynamic from 'next/dynamic';
import cookie from 'cookie';
import {gladiatorService} from '@jobseeker/common-core/services';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';
// const JobFeedHarness = dynamic(
//   () => import('@jobseeker/client-harness/components/JobHarness/JobfeedV2'),
//   {
//     loading: () => <Loader />,
//   }
// );
const AdsPanelHarness = memo(
  dynamic(
    () => import('@jobseeker/client-harness/components/AdsHarness/AdsPanelV2'),
    {
      ssr: false,
    }
  )
);
const TruncatedTags = ({data, truncateSize = 8}: any) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <>
      {data
        ?.slice(0, isExpanded ? data?.length : truncateSize)
        ?.map((value: {tagId: number; tagName: string}, index: number) => (
          <Link
            sx={{
              textDecoration: 'none',
              color: 'text.primary',
              ':hover': {color: 'primary.main'},
            }}
            key={`rec-tag-${index}-${value.tagId}`}
            href={`/k/${kebabCase(value.tagName)}-jobs`}
          >
            &nbsp;#{value.tagName}
          </Link>
        ))}
      {data?.length > truncateSize && (
        <Typography
          color={'primary'}
          variant="subtitle3"
          sx={{cursor: 'pointer'}}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <br />
          See {!isExpanded ? 'More' : 'Less'}
        </Typography>
      )}
    </>
  );
};

const TruncatedText = ({data, truncateSize}: any) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <>
      {data?.slice(0, isExpanded ? data?.length : truncateSize)}
      {data?.length > truncateSize && (
        <>
          {!isExpanded && '...'}
          <Typography
            color={'primary'}
            variant="subtitle3"
            sx={{cursor: 'pointer'}}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            &nbsp; &nbsp;See {!isExpanded ? 'More' : 'Less'}
          </Typography>
        </>
      )}
    </>
  );
};

const RecruiterPage = ({
  userIdSSR,
  // slug,
  filters,
  recruiterSSRData,
  recruiterId,
  url,
  isStale,
}: {
  userIdSSR: number;
  slug: string;
  filters: any;
  recruiterSSRData: any;
  recruiterId: any;
  url: string;
  isStale: boolean;
}) => {
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  const appDomain = process.env.NEXT_PUBLIC_APP_DOMAIN;
  const metaData = getConfigByApp(metaInfo, JOBFEED, true, RECRUITER_JOBFEED);
  const isLarge = useIsLargeDevice();
  // const recruiterId = parseInt(slug.split('-')[0]);

  usePageLoad(RECRUITER_JOBFEED);

  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();
  const [recruiterData, setRecruiterData] = useState<any>(recruiterSSRData);
  // const jobDetailStore = useSelector((state: AppState) => state.jobDetail);

  useValidateAuth(dispatch);

  // const {userId} = authStore || {};
  const router = useRouter();
  const {userId} = useSelector((state: AppState) => state.auth, shallowEqual);

  const [pageNumber, setPageNumber] = useState(filters?.page + 1);
  const hasTracked = useRef(false);
  const initialPageTracked = useRef(false);
  const hasPageNoChanged = useRef(false);

  // Reset tracking when page number changes
  useEffect(() => {
    hasPageNoChanged.current = true;
  }, [pageNumber]);

  useEffect(() => {
    if (hasTracked.current && !hasPageNoChanged.current) return;
    const actionType: string =
      pageNumber > filters.page + 1
        ? 'scroll'
        : initialPageTracked.current
        ? 'scroll'
        : 'view';

    const sessionUserId = userIdSSR || userId || '-1';

    hasTracked.current = true;
    hasPageNoChanged.current = false;

    TrackEventUBA('recruiterProfilePageView', actionType, {
      ref: router.query.ref || '',
      pageName: 'Recruiter ProfilePage',
      pageNo: pageNumber,
      recruiterID: Number(recruiterId) || '',
      sessionUsername: sessionUserId,
    });
    initialPageTracked.current = true;
  }, [userId, pageNumber]);

  useEffect(() => {
    if (!isAppIimjobs) {
      handleTracking('Recruiter profile View', {
        event_category: 'Offline Landing page',
        Type: 'view',
      });
    }
  }, []);

  useEffect(() => {
    if (!recruiterData) {
      jobfeedService
        .getRecruiterData(recruiterId)
        .then(data => setRecruiterData(data))
        .catch(console.error);
    }
  }, [recruiterId, recruiterData]);

  const recruiterCardData = useMemo(() => {
    let data: any = [];
    if (recruiterData.about) {
      data.push({
        title: 'About',
        subTitle: (
          <TruncatedText
            truncateSize={isLarge ? 150 : 180}
            data={recruiterData.about}
          />
        ),
      });
    }
    data = data.concat([
      {
        title: (
          <>
            Last Active:{' '}
            <Typography
              component={'span'}
              fontWeight={500}
              color={'text.primary'}
              fontSize={12}
            >
              {moment(recruiterData.lastActive).isValid()
                ? moment(recruiterData.lastActive).format('DD MMM YYYY')
                : recruiterData.lastActive}
            </Typography>
          </>
        ),
      },
      {
        title: (
          <>
            Location:{' '}
            <Typography
              component={'span'}
              fontWeight={500}
              color={'text.primary'}
              fontSize={12}
            >
              {recruiterData.location || 'N/A'}
            </Typography>
          </>
        ),
      },
      {
        title: 'Hiring for skills',
        subTitle: <TruncatedTags data={recruiterData?.recruiterTags} />,
      },
    ]);
    return data;
  }, [recruiterData]);

  const seoString = useMemo(() => {
    return `${recruiterData.designation} at ${
      recruiterData.organisation
    }${` - ${recruiterData.name}`} ${appDomain}`;
  }, [recruiterData]);

  const breadcrumbElements: any = isAppIimjobs
    ? [
        {
          name: 'Home',
          item: '/',
        },
        {
          name: "Recruiter's Page",
          // item: category?.tag
          //   ? `/c/${category?.tag
          //       .replace('&', '')
          //       .replace(/\s+/g, '-')
          //       .toLowerCase()}-jobs?&ref=breadcrumb_jd`
          //   : '',
        },
      ]
    : [];

  // const isSearchEnabled = useSelector(
  //   (state: AppState) => state.app.layout.searchOverlay,
  //   shallowEqual
  // );
  const seoDescriptionString =
    recruiterData && Array.isArray(recruiterData.recruiterTags)
      ? `${recruiterData?.name}, ${recruiterData?.designation} at ${recruiterData?.organisation} is hiring for multiple job vacancies in ${recruiterData?.recruiterTags[0]?.tagName}, ${recruiterData?.recruiterTags[1]?.tagName}, ${recruiterData?.recruiterTags[2]?.tagName} etc - ${appDomain}`
      : '';

  const theme = useWrappedTheme();

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout
          seoConfig={{
            title: seoString,
            ...{
              description: seoDescriptionString,
              canonical: `https://www.${appDomain}${url}`,
            },
            noindex: isStale,
            nofollow: isStale,
          }}
        />
        <NavLayout>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={breadcrumbElements}
          >
            {!isAppIimjobs && <Header recruiterData={recruiterData} />}

            <Grid container spacing={2}>
              {/* <InjectSearchOverlay isSearchEnabled={isSearchEnabled} /> */}
              {isAppIimjobs && (
                <Grid item xxs={12} sm={3} md={3} lg={2.3} xl={2}>
                  <UserProfileCard
                    variant={VersionVariants.VERSION_TWO}
                    mode={'RECRUITER'}
                    subtitle={`${recruiterData.designation} at ${recruiterData.organisation}`}
                    name={
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        justifyContent="center"
                        fontSize={theme.typography.pxToRem(14)}
                        fontWeight={600}
                      >
                        {recruiterData.name}
                        {recruiterData.linkedInUrl && (
                          <Link
                            href={recruiterData.linkedInUrl}
                            target="_blank"
                            lineHeight={0}
                          >
                            <FaLinkedin color="#0077B5" size={16} />
                          </Link>
                        )}
                      </Box>
                    }
                    profileImgUrl={
                      recruiterData.image || recruiterData.logoLink
                    }
                  />
                  <Box mt={2}>
                    <CTA
                      variant={VersionVariants.VERSION_SIX}
                      cardInfo={recruiterCardData}
                    />
                  </Box>
                </Grid>
              )}
              {isAppIimjobs && (
                <Grid item xxs={12} md sx={{maxWidth: '810px !important'}}>
                  {/* <JobFeedHarness
                  onLoadMore={() => setPageNo(pageNo + 1)}
                  routeToJobDetail={routeToJobDetail}
                  title={'Jobs Posted by the recruiter'}
                  feedOnly={true}
                  infiniteScroll={true}
                  injectSearchOverlay={getSearchOverlay}
                  variant={VersionVariants.VERSION_TWO}
                /> */}
                  <JobFeed
                    jobFeedType={RECRUITER_JOBFEED}
                    catOrTagId={recruiterId}
                    catOrTagTitle={
                      isAppIimjobs ? seoString : 'Jobs posted by the recruiter'
                    }
                    showProfileCard={false}
                    showFeatured={false}
                    showLearning={false}
                    showLearningCenterCTA={false}
                    preFilters={filters}
                    setPageNumber={setPageNumber}
                    feedOnly={true}
                  />
                </Grid>
              )}
              {!isAppIimjobs && (
                <Grid container spacing={1}>
                  <Grid
                    item
                    xxs={14}
                    md={7.75}
                    lg={7.9}
                    sx={{
                      mx: {xxs: 4, md: 6.75, lg: 9.5},
                      mr: {xxs: 1, md: 3, lg: 3.5},
                      my: {xxs: 4.5, md: 5.5, lg: 5.5},
                    }}
                  >
                    <JobFeed
                      jobFeedType={RECRUITER_JOBFEED}
                      catOrTagId={recruiterId}
                      catOrTagTitle={'Jobs Posted'}
                      showProfileCard={false}
                      showFeatured={true}
                      showLearning={true}
                      showLearningCenterCTA={false}
                      preFilters={filters}
                      feedOnly={true}
                    />
                  </Grid>
                  <Grid
                    item
                    xxs={2.75}
                    lg={2.5}
                    sx={{
                      mx: {md: 1, lg: 2.5},
                      my: {md: 7.25, lg: 7},
                      display: {xxs: 'none', md: 'block'},
                    }}
                  >
                    <AdsPanelHarness
                      variant={VersionVariants.VERSION_TWO}
                      showSmallBadges={false}
                      showFeatured={true}
                      showLearning={true}
                      showLearningCenterCTA={false}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <Chat />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  (store: {dispatch: (arg0: {type: string; payload: any}) => void}) =>
    async (context: GetServerSidePropsContext) => {
      const pageNo = parseInt(context.query?.page) || 0;
      const minExp = parseInt(context.query?.minexp) || 0;
      const maxExp = parseInt(context.query?.maxexp) || 0;
      const query = parseInt(context.query?.maxexp) || 0;
      const sort = context.query?.sort || null;
      const url = context?.resolvedUrl;

      let userId = -1;

      try {
        if (context.req?.headers?.cookie) {
          const cookies = cookie.parse(context.req.headers.cookie);

          if (cookies?.[COOKIE_LOGIN_AUTH]) {
            const validateInfo = await gladiatorService.validate('validate', {
              headers: {
                cookie: context.req?.headers?.cookie,
              },
            });

            userId = parseInt(validateInfo?.userId ?? '0', 10);
          }
        }
      } catch (err) {}

      const filters = {
        sort: sort ? [sort] : null,
        page: pageNo > 0 ? pageNo - 1 : 0,
        query: query,
        minexp: minExp,
        maxexp: maxExp,
      };

      const seoConfig = await loadSEO(
        CATEGORY,
        {title: '', description: ''},
        '',
        true,
        pageNo
      );
      store.dispatch({
        type: 'AppContext/set_current_page',
        payload: {page: RECRUITER_JOBFEED},
      });
      store.dispatch({
        type: 'AppContext/set_slug',
        payload: '',
      });
      const recruiterId = parseInt(context.params.slug.split('-')[0]);
      const recruiterSSRData = await jobfeedService.getRecruiterData(
        recruiterId
      );
      if (!recruiterSSRData.id) {
        return {
          notFound: true,
        };
      }
      const recSSrJobs: any =
        (await jobfeedService.getRecuiterJobFeed({
          recruiterId: recruiterId.toString(),
        })) || [];

      store.dispatch({
        type: 'job/jobFeed/set_jobfeed',
        payload: recSSrJobs,
      });
      const isStale = isRecruiterStale(recruiterSSRData?.lastJobPostedDate);
      return {
        props: {
          userIdSSR: userId,
          slug: context.params.slug,
          filters,
          seoConfig,
          recruiterSSRData,
          recruiterId,
          url,
          isStale,
        },
      };
    }
);

export default RecruiterPage;
